import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Button } from '@artstorefronts/ui';
import { RepSparkIcon, ArrowRightIcon } from './ReputationIcons';
import { formatBalance } from './tween-number';
import { buildTownSquareUrl, possessive } from '../../utils';
import { ReputationSourceRow } from './ReputationSourceRow';
import { MY_REPUTATION_SOURCES_QUERY, type ReputationSourceConnection } from './reputation-queries';
import type { TFn } from './rel-time';
import './reputation.css';

interface ReputationPopoverProps {
  balance: number;
  /**
   * `"your"` shows "Your reputation" (signed-in user's pill).
   * `"other"` shows "<name>'s reputation" (public profile display).
   * `"fallback"` shows the generic "Reputation points" heading.
   */
  headingKey: 'your' | 'other' | 'fallback';
  ownerName?: string;
}

export function ReputationPopover({ balance, headingKey, ownerName }: ReputationPopoverProps) {
  const { t } = useTranslation('reputation');

  // Viewer's top earning sources — the same mini-list town-square shows. Only the signed-in
  // pill ("your") renders it: the query is viewer-scoped, and popover content mounts lazily on
  // hover-open, so this fires at most once per open.
  const { data: sourcesData } = useQuery<{ myReputationSources: ReputationSourceConnection }>(
    MY_REPUTATION_SOURCES_QUERY,
    { variables: { first: 5 }, errorPolicy: 'all', skip: headingKey !== 'your' }
  );
  const topSources = (sourcesData?.myReputationSources.edges ?? []).map((edge) => edge.node);

  let heading: string;
  if (headingKey === 'your') heading = t('popover.your-heading');
  else if (headingKey === 'other' && ownerName)
    heading = t('popover.other-heading', { name: ownerName, namePossessive: possessive(ownerName) });
  else heading = t('popover.fallback-heading');

  const pointsUrl = buildTownSquareUrl('/points');

  return (
    <div className="rep-popover" role="dialog" aria-label={t('popover.fallback-heading')}>
      <div className="pop-head">
        <RepSparkIcon className="rep-spark" />
        <h4>{heading}</h4>
        <span className="pop-num">{formatBalance(balance)}</span>
      </div>
      {topSources.length > 0 && (
        <>
          <div className="pop-sec-label">{t('popover.sources-section')}</div>
          <div className="pop-sources">
            {topSources.map((node) => (
              <ReputationSourceRow key={node.id} node={node} t={t as unknown as TFn} compact />
            ))}
          </div>
          <a className="pop-sources-more" href={buildTownSquareUrl('/points#top-earners')}>
            {t('popover.sources-see-more')}
          </a>
        </>
      )}
      <div className="pop-sec-label">{t('popover.earn-section')}</div>
      <ul className="pop-rules">
        <li>
          <b>+2</b>
          <span>{t('popover.rule-post-like')}</span>
        </li>
        <li>
          <b>+1</b>
          <span>{t('popover.rule-comment-like')}</span>
        </li>
        <li>
          <b>+3</b>
          <span>{t('popover.rule-post-comment')}</span>
        </li>
        <li>
          <b>+20</b>
          <span>{t('popover.rule-critique-like')}</span>
        </li>
        <li>
          <b>+50</b>
          <span>{t('popover.rule-critique-valued')}</span>
        </li>
        <li>
          <b>+100</b>
          <span>{t('popover.rule-referral')}</span>
        </li>
      </ul>
      <Button asChild size="lg" className="pop-redeem-btn w-full mt-5">
        <a href={pointsUrl}>
          {t('popover.redeem-btn')}
          <ArrowRightIcon />
        </a>
      </Button>
    </div>
  );
}
